// ============================================================
// SALT - Password Strength Indicator
// ============================================================

import React from 'react';

interface PasswordStrengthProps {
  password: string;
}

interface StrengthResult {
  score: number;
  label: string;
  color: string;
  checks: {
    length: boolean;
    uppercase: boolean;
    lowercase: boolean;
    number: boolean;
    special: boolean;
  };
}

function getPasswordStrength(password: string): StrengthResult {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  };

  const passed = Object.values(checks).filter(Boolean).length;

  const levels = [
    { label: 'Very Weak', color: '#f87171' },
    { label: 'Weak', color: '#fb923c' },
    { label: 'Fair', color: '#fbbf24' },
    { label: 'Strong', color: '#4ade80' },
    { label: 'Very Strong', color: '#22c55e' },
  ];

  const score = Math.min(passed, 4);

  return {
    score,
    label: levels[score].label,
    color: levels[score].color,
    checks,
  };
}

export default function PasswordStrength({ password }: PasswordStrengthProps) {
  if (!password) return null;

  const strength = getPasswordStrength(password);

  const checkIcon = (passed: boolean) =>
    passed ? (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M3 6l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="2" fill="currentColor" opacity="0.4" />
      </svg>
    );

  return (
    <div className="sl-pw-strength">
      {/* Strength bar */}
      <div className="sl-pw-strength-bar">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="sl-pw-strength-segment"
            style={{
              backgroundColor: i <= strength.score - 1 ? strength.color : undefined,
            }}
          />
        ))}
      </div>

      {/* Label */}
      <span className="sl-pw-strength-label" style={{ color: strength.color }}>
        {strength.label}
      </span>

      {/* Requirements checklist */}
      <div className="sl-pw-checks">
        <div className={`sl-pw-check ${strength.checks.length ? 'sl-pw-check--pass' : ''}`}>
          {checkIcon(strength.checks.length)}
          <span>8+ characters</span>
        </div>
        <div className={`sl-pw-check ${strength.checks.uppercase ? 'sl-pw-check--pass' : ''}`}>
          {checkIcon(strength.checks.uppercase)}
          <span>Uppercase letter</span>
        </div>
        <div className={`sl-pw-check ${strength.checks.lowercase ? 'sl-pw-check--pass' : ''}`}>
          {checkIcon(strength.checks.lowercase)}
          <span>Lowercase letter</span>
        </div>
        <div className={`sl-pw-check ${strength.checks.number ? 'sl-pw-check--pass' : ''}`}>
          {checkIcon(strength.checks.number)}
          <span>Number</span>
        </div>
        <div className={`sl-pw-check ${strength.checks.special ? 'sl-pw-check--pass' : ''}`}>
          {checkIcon(strength.checks.special)}
          <span>Special character</span>
        </div>
      </div>
    </div>
  );
}
